import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled, type Theme } from "@mui/material/styles";
import { MarkReadIcon, ReadStateToggleIcon } from "_shared/_views/buttons/read-state-icons";
import { HOVER_ACTION_CLASS } from "messaging/correspondence/_views/message-hover-actions";

/**
 * The read-state controls both Correspondence panes draw: the rule down an unread message's left edge,
 * the per-message Mark Read / Mark Unread button, and Mark all read.
 *
 * They were the proposal pane's alone. The work agreement pane has them too now, from here, so the two
 * panes say "unread" the same way.
 */

/**
 * The rule down the left edge of an unread message (#5571). A read message keeps the border, transparent,
 * so its content does not move sideways when the rule comes and goes.
 */
export function unreadRule(theme: Theme, unread: boolean): string {
  return `3px solid ${unread ? theme.palette.primary.main : "transparent"}`;
}

type ReadStateButtonProps = {
  unread: boolean;
  /**
   * A read-only pane only lets a reader mark an unread message read. It offers no Mark Unread, and no
   * button at all on a read message.
   */
  readOnly?: boolean;
  disabled?: boolean;
  /** Pressed: mark the message read when it is unread, unread when it is read. */
  onToggle: () => void;
};

/** The mark an unread message carries beside its time: a dot, the size of a letter's bowl. */
const UnreadDot = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: "50%",
  display: "block",
  height: "8px",
  width: "8px",
}));

/**
 * The per-message read-state button. On an unread message it is a dot that says so, and pressed marks
 * the message read; an envelope beside each message read as a way to send it by email. On a read one it
 * is Mark Unread's icon (#6262), shown only while the reader points at the message or tabs into it.
 */
export function ReadStateButton(props: Readonly<ReadStateButtonProps>) {
  const { unread, readOnly, disabled, onToggle } = props;
  if (readOnly && !unread) return null;

  const title = readOnly || unread ? "Mark Read" : "Mark Unread";

  return (
    <Tooltip title={title}>
      <IconButton
        aria-label={title}
        className={unread ? undefined : HOVER_ACTION_CLASS}
        color="primary"
        disabled={disabled}
        size="small"
        onClick={(event) => {
          // The row is itself a Mark Read target while it is unread; the button is the only one pressed.
          event.stopPropagation();
          onToggle();
        }}
      >
        {unread ? <UnreadDot data-testid="unread-dot" /> : <ReadStateToggleIcon read fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
}

const MarkAllButton = styled(Button)(() => ({
  marginLeft: "auto",
  whiteSpace: "nowrap",
}));

/** Marks every unread message in the conversation read. Disabled while there is nothing unread. */
export function MarkAllReadButton(props: Readonly<{ disabled: boolean; onClick: () => void }>) {
  return (
    <MarkAllButton
      color="primary"
      size="small"
      disabled={props.disabled}
      startIcon={<MarkReadIcon />}
      variant="outlined"
      onClick={props.onClick}
    >
      Mark all read
    </MarkAllButton>
  );
}
